import { useMemo, useRef } from 'react';

import useChartFunctions from '@/components/Chart/hooks/options/useChartFunctions';
import type { PreviewChartProps } from '@/features/DataHub/constants/charts';

import { useTheme } from '@mui/material';
import type Highcharts from 'highcharts/highstock';
import type { ChartEventsOptions, XAxisOptions } from 'highcharts';

import PreviewChartLayout from './PreviewChartLayout';
import usePreviewOptions from './usePreviewOptions';
import useSpotChartData from './useSpotChartData';

const PreviewFullscreenChart = ({ symbols, showLegend, isLightMode }: PreviewChartProps) => {
  const { loading, error, chartSeries, baseOptions, xAxisMin, xAxisMax } =
    useSpotChartData(symbols);

  const theme = useTheme();
  const textColor = isLightMode ? theme.palette.background.paper : theme.palette.text.primary;
  const backgroundColor = isLightMode ? theme.palette.text.primary : theme.palette.background.paper;
  const previewOptions = usePreviewOptions(baseOptions, textColor, backgroundColor);

  const { createEventOptions } = useChartFunctions();
  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);
  const getWatermarkAttributes = (chart: Highcharts.Chart) => {
    return {
      width: 190,
      height: 50,
      x: Math.max(0, chart.chartWidth - 250),
      y: Math.max(0, chart.chartHeight - 220),
      zIndex: 1
    };
  };
  const events: ChartEventsOptions = createEventOptions(
    watermarkImage,
    getWatermarkAttributes,
    isLightMode ? '/watermark-color.svg' : '/watermark-black.svg'
  );

  const options = useMemo(() => {
    if (!previewOptions) return undefined;

    return {
      ...previewOptions,
      chart: { ...previewOptions.chart, events },
      legend: { ...previewOptions.legend, enabled: showLegend },
      xAxis: {
        ...previewOptions.xAxis,
        labels: {
          ...(previewOptions?.xAxis as XAxisOptions).labels,
          style: { ...(previewOptions?.xAxis as XAxisOptions).labels?.style, color: textColor }
        },
        min: xAxisMin,
        max: xAxisMax
      }
    };
  }, [previewOptions, xAxisMin, xAxisMax]);

  return (
    <PreviewChartLayout
      chartSeries={chartSeries}
      chartType={'spot'}
      error={error}
      loading={loading}
      options={options}
    />
  );
};

export default PreviewFullscreenChart;
